import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Row, Col, Card, Form, Button, Table,
  Modal, Badge, Alert, Spinner, Image,
  Dropdown, DropdownButton, InputGroup,
  ProgressBar, Tabs, Tab
} from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import axios from 'axios';
import AdminNavbar from './components/AdminNavbar';
import { BASE_URL, UPLOAD_BASE_URL } from '../../services/api';
import { 
  FaUserTie, FaGraduationCap, FaIdCard, FaPhone, 
  FaEnvelope, FaMapMarkerAlt, FaCalendarAlt, FaVenusMars,
  FaBriefcase, FaUniversity, FaAward, FaFilePdf,
  FaEdit, FaTrash, FaEye, FaPlus, FaFilter,
  FaSort, FaSearch, FaDownload, FaUpload
} from 'react-icons/fa';

const StaffManager = () => {
  const [staff, setStaff] = useState([]);
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [user, setUser] = useState(null);
  
  // Form states
  const [showModal, setShowModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [tempQualifications, setTempQualifications] = useState([]);
  const [qualificationsToDelete, setQualificationsToDelete] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  
  // Options
  const [departments, setDepartments] = useState([]);
  const [stats, setStats] = useState([]);
  
  // API configuration
  const API_BASE_URL = BASE_URL;
  
  // Category options
  const categoryOptions = [
    'TEACHING STAFF',
    'NON TEACHING STAFF',
    'ADMINSTRATIVE STAFF',
    'Other'
  ];
  
  // Gender options
  const genderOptions = ['Male', 'Female', 'Other'];
  
  const [formData, setFormData] = useState({
    staff_code: '',
    category_type: 'TEACHING STAFF',
    department_id: '',
    name: '',
    designation: '',
    specialization: '',
    experience_years: 0,
    gender: 'Male',
    dob: '',
    phone: '',
    email: '',
    address: '',
    join_date: '',
    profile_photo: '',
    awards_honors: '',
    status: 1,
    qualifications: []
  });

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || 'null');
    setUser(userData);
    fetchStaff();
    fetchDepartments();
    fetchStats();
  }, []);

  useEffect(() => {
    filterStaff();
  }, [staff, searchTerm, statusFilter, categoryFilter, departmentFilter]);

  const fetchStaff = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_BASE_URL}/staff.php`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setStaff(response.data.data);
        setFilteredStaff(response.data.data);
      } else {
        setError('Failed to load staff: ' + response.data.message);
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Error connecting to server: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/staff.php?departments=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setDepartments(response.data.data);
      }
    } catch (err) {
      console.error('Fetch departments error:', err);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/staff.php?stats=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (err) {
      console.error('Fetch stats error:', err);
    }
  };

  const filterStaff = () => {
    let filtered = staff;
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(staffMember =>
        staffMember.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        staffMember.staff_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        staffMember.designation.toLowerCase().includes(searchTerm.toLowerCase()) ||
        staffMember.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      const statusValue = statusFilter === 'active' ? 1 : 0;
      filtered = filtered.filter(staffMember => staffMember.status == statusValue);
    }
    
    // Apply category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(staffMember => staffMember.category_type === categoryFilter);
    }
    
    // Apply department filter
    if (departmentFilter !== 'all') {
      filtered = filtered.filter(staffMember => 
        staffMember.department_id == departmentFilter
      );
    }
    
    setFilteredStaff(filtered);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleQualificationAdd = () => {
    const newQualification = {
      id: `temp-${Date.now()}`,
      qualification_name: '',
      institution: '',
      year_completed: '',
      document_path: '',
      display_order: tempQualifications.length + 1
    };
    setTempQualifications(prev => [...prev, newQualification]);
  };

  const handleQualificationChange = (index, field, value) => {
    const updatedQualifications = [...tempQualifications];
    updatedQualifications[index][field] = value;
    setTempQualifications(updatedQualifications);
  };

  const handleQualificationRemove = (index) => {
    setTempQualifications(prev => prev.filter((_, i) => i !== index));
  };

  const handleQualificationDragEnd = (result) => {
    if (!result.destination) return;
    
    const qualifications = editingStaff 
      ? [...formData.qualifications] 
      : [...tempQualifications];
    
    const [reorderedItem] = qualifications.splice(result.source.index, 1);
    qualifications.splice(result.destination.index, 0, reorderedItem);
    
    // Update order numbers
    const updatedQualifications = qualifications.map((qual, index) => ({
      ...qual,
      display_order: index + 1
    }));
    
    if (editingStaff) {
      setFormData(prev => ({
        ...prev,
        qualifications: updatedQualifications
      }));
    } else {
      setTempQualifications(updatedQualifications);
    }
  };

  const handleFileUpload = async (file, type) => {
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('file_type', type);
      
      const response = await axios.post(
        `${API_BASE_URL}/upload_staff_documents.php`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          }
        }
      );
      
      if (response.data.success) {
        return response.data.file_path;
      }
      return null;
    } catch (err) {
      console.error('Upload error:', err);
      setError('Failed to upload file: ' + err.message);
      return null;
    }
  };

  const handleProfilePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploading(true);
      const filePath = await handleFileUpload(file, 'profile');
      if (filePath) {
        setFormData(prev => ({ ...prev, profile_photo: filePath }));
        setSuccess('Profile photo uploaded successfully!');
        setTimeout(() => setSuccess(''), 3000);
      }
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleQualificationDocChange = async (e, index) => {
    const file = e.target.files[0];
    if (file) {
      setUploading(true);
      const filePath = await handleFileUpload(file, 'document');
      if (filePath) {
        handleQualificationChange(index, 'document_path', filePath);
        setSuccess('Document uploaded successfully!');
        setTimeout(() => setSuccess(''), 3000);
      }
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const validateForm = () => {
    // Basic validation
    if (!formData.name.trim()) {
      setError('Name is required');
      return false;
    }
    
    if (!formData.designation.trim()) {
      setError('Designation is required');
      return false;
    }
    
    // Check for invalid qualification names in temp qualifications
    for (const qual of tempQualifications) {
      if (qual.qualification_name === '') {
        setError('Please fill in all qualification names or remove empty qualifications');
        return false;
      }
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const token = localStorage.getItem('token');
      let dataToSend = { ...formData };
      
      // Convert empty department_id to null
      if (dataToSend.department_id === '') {
        dataToSend.department_id = null;
      }
      
      // Convert experience_years to number
      dataToSend.experience_years = parseInt(dataToSend.experience_years) || 0;
      
      if (editingStaff) {
        // For editing, handle qualifications updates
        dataToSend.qualifications_to_add = tempQualifications.filter(q => q.qualification_name.trim() !== '');
        dataToSend.qualifications_to_delete = qualificationsToDelete;
        
        // Reordering info
        if (formData.qualifications.length > 0) {
          const qualificationsOrder = formData.qualifications.map((qual, index) => ({
            id: qual.id,
            display_order: index + 1
          }));
          dataToSend.qualifications_order = qualificationsOrder;
        }
        
        delete dataToSend.qualifications; // Remove original qualifications array
        
        const response = await axios.put(
          `${API_BASE_URL}/staff.php?id=${editingStaff.id}`,
          dataToSend,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (response.data.success) {
          setSuccess('Staff member updated successfully!');
          setShowModal(false);
          resetForm();
          setEditingStaff(null);
          fetchStaff();
          fetchStats();
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError('Operation failed: ' + (response.data.message || ''));
        }
      } else {
        // For new staff, include temp qualifications
        dataToSend.qualifications = tempQualifications.filter(q => q.qualification_name.trim() !== '');
        
        // Remove temporary IDs
        dataToSend.qualifications = dataToSend.qualifications.map(({ id, ...rest }) => rest);
        
        const response = await axios.post(
          `${API_BASE_URL}/staff.php`,
          dataToSend,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (response.data.success) {
          setSuccess('Staff member added successfully!');
          setShowModal(false);
          resetForm();
          fetchStaff();
          fetchStats();
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError('Operation failed: ' + (response.data.message || ''));
        }
      }
    } catch (err) {
      console.error('Submit error:', err);
      setError('Error saving staff member: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this staff member? All associated documents will also be deleted.')) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.delete(
          `${API_BASE_URL}/staff.php?id=${id}`,
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );
        
        if (response.data.success) {
          setSuccess('Staff member deleted successfully!');
          fetchStaff();
          fetchStats();
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError('Failed to delete staff member: ' + (response.data.message || ''));
        }
      } catch (err) {
        console.error('Delete error:', err);
        setError('Error deleting staff member: ' + err.message);
      }
    }
  };

  const handleEdit = (staffMember) => {
    setEditingStaff(staffMember);
    setFormData({
      staff_code: staffMember.staff_code,
      category_type: staffMember.category_type,
      department_id: staffMember.department_id || '',
      name: staffMember.name,
      designation: staffMember.designation || '',
      specialization: staffMember.specialization || '',
      experience_years: staffMember.experience_years || 0,
      gender: staffMember.gender || 'Male',
      dob: staffMember.dob || '',
      phone: staffMember.phone || '',
      email: staffMember.email || '',
      address: staffMember.address || '',
      join_date: staffMember.join_date || '',
      profile_photo: staffMember.profile_photo || '',
      awards_honors: staffMember.awards_honors || '',
      status: staffMember.status,
      qualifications: staffMember.qualifications || []
    });
    setTempQualifications([]);
    setQualificationsToDelete([]);
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      staff_code: '',
      category_type: 'TEACHING STAFF',
      department_id: '',
      name: '',
      designation: '',
      specialization: '',
      experience_years: 0,
      gender: 'Male',
      dob: '',
      phone: '',
      email: '',
      address: '',
      join_date: '',
      profile_photo: '',
      awards_honors: '',
      status: 1,
      qualifications: []
    });
    setTempQualifications([]);
    setQualificationsToDelete([]);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingStaff(null);
    resetForm();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const calculateAge = (dob) => {
    if (!dob) return 'N/A';
    try {
      const birthDate = new Date(dob);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age;
    } catch {
      return 'N/A';
    }
  };

  const getImageUrl = (path) => {
    if (!path) return null;
    return `${UPLOAD_BASE_URL}${path}`;
  };

  const getDepartmentName = (departmentId) => {
    const department = departments.find(d => d.id == departmentId);
    return department ? department.department_name : 'Not assigned';
  };

  if (loading) {
    return (
      <>
        <AdminNavbar user={user} />
        <Container className="mt-5 text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading staff members...</p>
        </Container>
      </>
    );
  }

  return (
    <>
      <AdminNavbar user={user} />
      <Container fluid className="py-4">
        <Row className="mb-4">
          <Col>
            <h2>Staff Management</h2>
            <p className="text-muted">Manage teaching, non-teaching and administrative staff</p>
          </Col>
          <Col className="text-end">
            <Button variant="primary" onClick={() => setShowModal(true)}>
              <FaPlus className="me-2" /> Add Staff
            </Button>
          </Col>
        </Row>

        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
        {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

        {/* Statistics Cards */}
        <Row className="mb-4">
          {stats.map((stat, index) => (
            <Col key={index} lg={3} md={6} className="mb-3">
              <Card className="border-0 shadow-sm">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="mb-1">{stat.category_type}</h6>
                      <h4 className="mb-0">{stat.count}</h4>
                      <small className="text-muted">
                        <Badge bg="success" className="me-1">{stat.active_count} Active</Badge>
                        <Badge bg="secondary">{stat.count - stat.active_count} Inactive</Badge>
                      </small>
                    </div>
                    <div className="text-primary fs-2">
                      <FaUserTie />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Search and Filters */}
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Body>
            <Row className="g-3">
              <Col lg={4}>
                <InputGroup>
                  <InputGroup.Text>
                    <FaSearch />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Search by name, staff code, designation or email"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col lg={2}>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Form.Select>
              </Col>
              <Col lg={2}>
                <Form.Select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <option value="all">All Categories</option>
                  {categoryOptions.map((category, idx) => (
                    <option key={idx} value={category}>{category}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col lg={2}>
                <Form.Select
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                >
                  <option value="all">All Departments</option>
                  {departments.map((dept, idx) => (
                    <option key={idx} value={dept.id}>{dept.department_name}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col lg={2}>
                <Button
                  variant="outline-secondary"
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                    setCategoryFilter('all');
                    setDepartmentFilter('all');
                  }}
                  type="button"
                >
                  <FaFilter className="me-1" /> Clear Filters
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Staff Table */}
        <Card className="border-0 shadow-sm">
          <Card.Body>
            <div className="table-responsive">
              <Table hover>
                <thead>
                  <tr>
                    <th width="80">Photo</th>
                    <th>Staff Code</th>
                    <th>Name</th>
                    <th>Designation</th>
                    <th>Department</th>
                    <th>Category</th>
                    <th>Experience</th>
                    <th>Status</th>
                    <th width="120">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStaff.length > 0 ? (
                    filteredStaff.map((staffMember) => (
                      <tr key={staffMember.id}>
                        <td>
                          {staffMember.profile_photo ? (
                            <Image
                              src={getImageUrl(staffMember.profile_photo)}
                              alt={staffMember.name}
                              roundedCircle
                              width={50}
                              height={50}
                              className="border"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = `https://ui-avatars.com/api/?name=${staffMember.name}&background=random`;
                              }}
                            />
                          ) : (
                            <div 
                              className="rounded-circle bg-light d-flex align-items-center justify-content-center"
                              style={{ width: 50, height: 50 }}
                            >
                              <FaUserTie className="text-muted" />
                            </div>
                          )}
                        </td>
                        <td>
                          <Badge bg="info" className="mb-1">{staffMember.staff_code}</Badge>
                          <div className="small text-muted">
                            <FaCalendarAlt className="me-1" />
                            Joined: {formatDate(staffMember.join_date)}
                          </div>
                        </td>
                        <td>
                          <strong>{staffMember.name}</strong>
                          <div className="small text-muted">
                            <FaEnvelope className="me-1" /> {staffMember.email || 'N/A'}
                          </div>
                          <div className="small text-muted">
                            <FaPhone className="me-1" /> {staffMember.phone || 'N/A'}
                          </div>
                        </td>
                        <td>
                          <div>{staffMember.designation || 'N/A'}</div>
                          {staffMember.specialization && (
                            <small className="text-muted">{staffMember.specialization}</small>
                          )}
                        </td>
                        <td>
                          {getDepartmentName(staffMember.department_id)}
                        </td>
                        <td>
                          <Badge 
                            bg={staffMember.category_type === 'TEACHING STAFF' ? 'primary' : 
                                staffMember.category_type === 'NON TEACHING STAFF' ? 'secondary' : 
                                'dark'}
                          >
                            {staffMember.category_type}
                          </Badge>
                          <div className="small text-muted mt-1">
                            <FaVenusMars className="me-1" /> {staffMember.gender || 'N/A'}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <FaBriefcase className="text-warning me-2" />
                            <div>
                              <strong>{staffMember.experience_years || 0} years</strong>
                              <div className="small text-muted">
                                Age: {calculateAge(staffMember.dob)}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <Badge bg={staffMember.status == 1 ? 'success' : 'secondary'}>
                            {staffMember.status == 1 ? 'Active' : 'Inactive'}
                          </Badge>
                          {staffMember.qualifications && staffMember.qualifications.length > 0 && (
                            <div className="small text-muted mt-1">
                              <FaGraduationCap className="me-1" />
                              {staffMember.qualifications.length} qual.
                            </div>
                          )}
                        </td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="me-2"
                            onClick={() => handleEdit(staffMember)}
                            type="button"
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => handleDelete(staffMember.id)}
                            type="button"
                          >
                            <FaTrash />
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="text-center py-5">
                        <FaUserTie size={48} className="text-muted mb-3" />
                        <h5 className="mb-2">No Staff Members Found</h5>
                        <p className="text-muted mb-0">
                          {searchTerm 
                            ? `No staff members match "${searchTerm}"`
                            : "No staff members found with current filters"}
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
            
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div className="text-muted">
                Showing {filteredStaff.length} of {staff.length} staff members
              </div>
              <Button 
                variant="outline-primary" 
                size="sm"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                  setCategoryFilter('all');
                  setDepartmentFilter('all');
                }}
                type="button"
              >
                Show All Staff
              </Button>
            </div>
          </Card.Body>
        </Card>

        {/* Add/Edit Staff Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size="xl" scrollable>
          <Modal.Header closeButton>
            <Modal.Title>
              {editingStaff ? 'Edit Staff Member' : 'Add New Staff Member'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit} noValidate>
            <Modal.Body>
              <Tabs defaultActiveKey="basic" className="mb-3">
                <Tab eventKey="basic" title="Basic Information">
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Staff Code</Form.Label>
                        <Form.Control
                          type="text"
                          name="staff_code"
                          value={formData.staff_code}
                          onChange={handleInputChange}
                          placeholder="Auto-generated if empty"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Category Type *</Form.Label>
                        <Form.Select
                          name="category_type"
                          value={formData.category_type}
                          onChange={handleInputChange}
                          required
                        >
                          {categoryOptions.map((category, idx) => (
                            <option key={idx} value={category}>{category}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Full Name *</Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Designation *</Form.Label>
                        <Form.Control
                          type="text"
                          name="designation"
                          value={formData.designation}
                          onChange={handleInputChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                    
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Specialization</Form.Label>
                        <Form.Control
                          type="text"
                          name="specialization"
                          value={formData.specialization}
                          onChange={handleInputChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Experience (Years)</Form.Label>
                        <Form.Control
                          type="number"
                          name="experience_years"
                          value={formData.experience_years}
                          onChange={handleInputChange}
                          min="0"
                          max="60"
                        />
                      </Form.Group>
                    </Col>
                    
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Gender</Form.Label>
                        <Form.Select
                          name="gender"
                          value={formData.gender}
                          onChange={handleInputChange}
                        >
                          {genderOptions.map((gender, idx) => (
                            <option key={idx} value={gender}>{gender}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Date of Birth</Form.Label>
                        <Form.Control
                          type="date"
                          name="dob"
                          value={formData.dob}
                          onChange={handleInputChange}
                          max={new Date().toISOString().split('T')[0]}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Department</Form.Label>
                        <Form.Select
                          name="department_id"
                          value={formData.department_id}
                          onChange={handleInputChange}
                        >
                          <option value="">Select Department (Optional)</option>
                          {departments.map((dept, idx) => (
                            <option key={idx} value={dept.id}>{dept.department_name}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                </Tab>
                
                <Tab eventKey="contact" title="Contact Information">
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Email Address</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Phone Number</Form.Label>
                        <Form.Control
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                        />
                      </Form.Group>
                    </Col>
                    
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>Address</Form.Label>
                        <Form.Control
                          as="textarea"
                          name="address"
                          value={formData.address}
                          onChange={handleInputChange}
                          rows={3}
                        />
                      </Form.Group>
                    </Col>
                    
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Join Date</Form.Label>
                        <Form.Control
                          type="date"
                          name="join_date"
                          value={formData.join_date}
                          onChange={handleInputChange}
                          max={new Date().toISOString().split('T')[0]}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Status</Form.Label>
                        <Form.Select
                          name="status"
                          value={formData.status}
                          onChange={handleInputChange}
                        >
                          <option value="1">Active</option>
                          <option value="0">Inactive</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                </Tab>
                
                <Tab eventKey="profile" title="Profile Photo">
                  <Row>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Upload Profile Photo</Form.Label>
                        <Form.Control
                          type="file"
                          accept="image/*"
                          onChange={handleProfilePhotoChange}
                          disabled={uploading || isSubmitting}
                        />
                        <Form.Text className="text-muted">
                          Recommended: Square image, max 2MB (JPG, PNG, GIF, WebP)
                        </Form.Text>
                      </Form.Group>
                      
                      {uploading && (
                        <div className="mt-3">
                          <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} />
                          <small className="text-muted">Uploading...</small>
                        </div>
                      )}
                    </Col>
                    <Col md={6}>
                      {formData.profile_photo ? (
                        <div className="text-center">
                          <Image
                            src={getImageUrl(formData.profile_photo)}
                            alt="Profile Preview"
                            roundedCircle
                            width={150}
                            height={150}
                            className="border shadow-sm"
                            style={{ objectFit: 'cover' }}
                          />
                          <div className="mt-2">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => setFormData(prev => ({ ...prev, profile_photo: '' }))}
                              type="button"
                              disabled={isSubmitting}
                            >
                              Remove Photo
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center border rounded p-4 bg-light">
                          <FaUserTie size={64} className="text-muted mb-3" />
                          <p className="text-muted mb-0">No profile photo selected</p>
                        </div>
                      )}
                    </Col>
                  </Row>
                </Tab>
                
                <Tab eventKey="qualifications" title="Qualifications">
                  <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6>Qualifications & Documents</h6>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={handleQualificationAdd}
                        type="button"
                        disabled={isSubmitting}
                      >
                        <FaPlus className="me-1" /> Add Qualification
                      </Button>
                    </div>
                    
                    <DragDropContext onDragEnd={handleQualificationDragEnd}>
                      <Droppable droppableId="qualifications">
                        {(provided) => (
                          <div ref={provided.innerRef} {...provided.droppableProps}>
                            {/* Existing Qualifications */}
                            {formData.qualifications.map((qualification, index) => (
                              <Draggable 
                                key={qualification.id} 
                                draggableId={qualification.id.toString()} 
                                index={index}
                              >
                                {(provided) => (
                                  <Card 
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    className="mb-3 border qualification-card"
                                  >
                                    <Card.Body>
                                      <div className="d-flex justify-content-between align-items-start mb-3">
                                        <div {...provided.dragHandleProps} style={{ cursor: 'move' }}>
                                          <Badge bg="light" text="dark">
                                            <FaSort className="me-1" /> Qualification #{index + 1}
                                          </Badge>
                                        </div>
                                        <Button
                                          variant="outline-danger"
                                          size="sm"
                                          onClick={() => {
                                            if (qualification.id && !qualification.id.toString().startsWith('temp-')) {
                                              setQualificationsToDelete(prev => [...prev, qualification.id]);
                                            }
                                            const newQualifications = formData.qualifications.filter((_, i) => i !== index);
                                            setFormData(prev => ({ ...prev, qualifications: newQualifications }));
                                          }}
                                          type="button"
                                          disabled={isSubmitting}
                                        >
                                          <FaTrash />
                                        </Button>
                                      </div>
                                      
                                      <Row className="g-3">
                                        <Col md={5}>
                                          <Form.Group>
                                            <Form.Label>Qualification Name</Form.Label>
                                            <Form.Control
                                              type="text"
                                              value={qualification.qualification_name}
                                              onChange={(e) => {
                                                const newQualifications = [...formData.qualifications];
                                                newQualifications[index].qualification_name = e.target.value;
                                                setFormData(prev => ({ ...prev, qualifications: newQualifications }));
                                              }}
                                              required
                                            />
                                          </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                          <Form.Group>
                                            <Form.Label>Institution</Form.Label>
                                            <Form.Control
                                              type="text"
                                              value={qualification.institution}
                                              onChange={(e) => {
                                                const newQualifications = [...formData.qualifications];
                                                newQualifications[index].institution = e.target.value;
                                                setFormData(prev => ({ ...prev, qualifications: newQualifications }));
                                              }}
                                            />
                                          </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                          <Form.Group>
                                            <Form.Label>Year Completed</Form.Label>
                                            <Form.Control
                                              type="number"
                                              min="1900"
                                              max={new Date().getFullYear()}
                                              value={qualification.year_completed}
                                              onChange={(e) => {
                                                const newQualifications = [...formData.qualifications];
                                                newQualifications[index].year_completed = e.target.value;
                                                setFormData(prev => ({ ...prev, qualifications: newQualifications }));
                                              }}
                                            />
                                          </Form.Group>
                                        </Col>
                                        
                                        <Col md={12}>
                                          <Form.Group>
                                            <Form.Label>Document</Form.Label>
                                            <div className="d-flex align-items-center">
                                              {qualification.document_path ? (
                                                <>
                                                  <div className="flex-grow-1">
                                                    <Badge bg="success" className="me-2">
                                                      <FaFilePdf className="me-1" /> Document Uploaded
                                                    </Badge>
                                                    <small className="text-muted">
                                                      {qualification.document_path.split('/').pop()}
                                                    </small>
                                                  </div>
                                                  <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={() => {
                                                      const newQualifications = [...formData.qualifications];
                                                      newQualifications[index].document_path = '';
                                                      setFormData(prev => ({ ...prev, qualifications: newQualifications }));
                                                    }}
                                                    type="button"
                                                    disabled={isSubmitting}
                                                  >
                                                    Remove
                                                  </Button>
                                                </>
                                              ) : (
                                                <div>
                                                  <Form.Control
                                                    type="file"
                                                    accept=".pdf,.doc,.docx"
                                                    onChange={(e) => {
                                                      alert('To upload a document, please use the Add Qualification section below');
                                                    }}
                                                    disabled={isSubmitting}
                                                  />
                                                  <Form.Text className="text-muted">
                                                    Upload new documents in the "Add Qualification" section below
                                                  </Form.Text>
                                                </div>
                                              )}
                                            </div>
                                          </Form.Group>
                                        </Col>
                                      </Row>
                                    </Card.Body>
                                  </Card>
                                )}
                              </Draggable>
                            ))}
                            
                            {/* Temporary Qualifications */}
                            {tempQualifications.map((qualification, index) => (
                              <Card key={qualification.id} className="mb-3 border qualification-card">
                                <Card.Body>
                                  <div className="d-flex justify-content-between align-items-start mb-3">
                                    <Badge bg="warning" text="dark">
                                      <FaPlus className="me-1" /> New Qualification
                                    </Badge>
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() => handleQualificationRemove(index)}
                                      type="button"
                                      disabled={isSubmitting}
                                    >
                                      <FaTrash />
                                    </Button>
                                  </div>
                                  
                                  <Row className="g-3">
                                    <Col md={5}>
                                      <Form.Group>
                                        <Form.Label>Qualification Name</Form.Label>
                                        <Form.Control
                                          type="text"
                                          value={qualification.qualification_name}
                                          onChange={(e) => handleQualificationChange(index, 'qualification_name', e.target.value)}
                                          required
                                        />
                                      </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                      <Form.Group>
                                        <Form.Label>Institution</Form.Label>
                                        <Form.Control
                                          type="text"
                                          value={qualification.institution}
                                          onChange={(e) => handleQualificationChange(index, 'institution', e.target.value)}
                                        />
                                      </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                      <Form.Group>
                                        <Form.Label>Year Completed</Form.Label>
                                        <Form.Control
                                          type="number"
                                          min="1900"
                                          max={new Date().getFullYear()}
                                          value={qualification.year_completed}
                                          onChange={(e) => handleQualificationChange(index, 'year_completed', e.target.value)}
                                        />
                                      </Form.Group>
                                    </Col>
                                    
                                    <Col md={12}>
                                      <Form.Group>
                                        <Form.Label>Document (Optional)</Form.Label>
                                        <div className="d-flex align-items-center">
                                          {qualification.document_path ? (
                                            <>
                                              <div className="flex-grow-1">
                                                <Badge bg="success" className="me-2">
                                                  <FaFilePdf className="me-1" /> Document Uploaded
                                                </Badge>
                                                <small className="text-muted">
                                                  {qualification.document_path.split('/').pop()}
                                                </small>
                                              </div>
                                              <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={() => handleQualificationChange(index, 'document_path', '')}
                                                type="button"
                                                disabled={isSubmitting}
                                              >
                                                Remove
                                              </Button>
                                            </>
                                          ) : (
                                            <>
                                              <Form.Control
                                                type="file"
                                                accept=".pdf,.doc,.docx"
                                                onChange={(e) => handleQualificationDocChange(e, index)}
                                                className="me-2"
                                                disabled={uploading || isSubmitting}
                                              />
                                              {uploading && (
                                                <Spinner animation="border" size="sm" />
                                              )}
                                            </>
                                          )}
                                        </div>
                                      </Form.Group>
                                    </Col>
                                  </Row>
                                </Card.Body>
                              </Card>
                            ))}
                            
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                    
                    {(formData.qualifications.length === 0 && tempQualifications.length === 0) && (
                      <div className="text-center py-5 border rounded bg-light">
                        <FaGraduationCap size={48} className="text-muted mb-3" />
                        <h6 className="mb-2">No Qualifications Added</h6>
                        <p className="text-muted mb-3">Add qualifications to complete the staff profile</p>
                        <Button
                          variant="outline-primary"
                          onClick={handleQualificationAdd}
                          type="button"
                          disabled={isSubmitting}
                        >
                          <FaPlus className="me-1" /> Add First Qualification
                        </Button>
                      </div>
                    )}
                  </div>
                </Tab>
                
                <Tab eventKey="awards" title="Awards & Honors">
                  <Form.Group>
                    <Form.Label>Awards, Honors & Achievements</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="awards_honors"
                      value={formData.awards_honors}
                      onChange={handleInputChange}
                      rows={8}
                      placeholder="List awards, honors, publications, research work, etc. (One per line or separate with commas)"
                      disabled={isSubmitting}
                    />
                    <Form.Text className="text-muted">
                      Enter each award/honor on a new line or separate with commas
                    </Form.Text>
                  </Form.Group>
                </Tab>
              </Tabs>
            </Modal.Body>
            <Modal.Footer>
              <Button 
                variant="secondary" 
                onClick={handleCloseModal}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button 
                variant="primary" 
                type="submit" 
                disabled={isSubmitting || uploading}
              >
                {isSubmitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {editingStaff ? 'Updating...' : 'Adding...'}
                  </>
                ) : editingStaff ? 'Update Staff Member' : 'Add Staff Member'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </>
  );
};

export default StaffManager;